import React, { useEffect, useState, Fragment } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AES } from "crypto-js";

import {
  getCustomerCardsService,
  deleteCardService,
  markPrimaryCard
} from "../../services/programService";

import AddNewCard from "./AddNewCard";
import SecureInput from "./SecureInput";
import CreditCard from "./CreditCard";
import CustomDropDown from "../reuse/UIComponents/CustomDropDown";
import { accountTypes } from "../../helper";
import settings from "../../config/settings";
import { ProgramLoader } from "../reuse/SVG";

const GenericPaymentGateway = ({
  creditCardPayInfo,
  internetBankPayInfo,
  campCode,
  userId,
  userEmail,
  onSubmit,
  onCancel
}) => {
  const [paymentMethod, setPaymentMethod] = useState(0);
  const [cardList, setCardList] = useState([]);
  const [isNewCard, setIsNewCard] = useState(false);
  const [showDeletedCards, setShowDeletedCards] = useState(false);
  const [loading, setLoading] = useState(false);

  const [customerPaymentProfileId, setCustomerPaymentProfileId] = useState("");
  const [customerProfileId, setCustomerProfileId] = useState("");

  // ACH fields
  const [bankAccountType, setBankAccountType] = useState("");
  const [bankAccountNum, setBankAccountNum] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [nameOnAccount, setNameOnAccount] = useState("");

  /* ---------------- Fetch Cards ---------------- */
  const getCards = () => {
    setLoading(true);
    const query = `?isWeb=1&campCode=${campCode}&includeDeleted=${showDeletedCards}`;

    getCustomerCardsService(query)
      .then(res => {
        if (res?.data?.statusCode && res.data.responseData.result?.length) {
          const cards = res.data.responseData.result;
          setCardList(cards);
          setCustomerProfileId(cards[0]?.card?.signature || "");
          setCustomerPaymentProfileId(cards[0]?.card?.token || "");
        } else {
          setIsNewCard(true);
        }
      })
      .finally(() => setLoading(false));
  };

  const isPayDisabled = paymentMethod === 0 || (paymentMethod === 4 && (!bankAccountType || !bankAccountNum || !routingNumber || !nameOnAccount));


  useEffect(() => {
    if (campCode) getCards();
  }, [campCode, showDeletedCards]);

  /* ---------------- Submit Payment ---------------- */
  const handleSubmit = () => {
    let authPayment = {};
    let token = "";

    if (paymentMethod === 1) {
      authPayment = {
        paymentType: 1,
        // customerProfileId,
        token: customerPaymentProfileId
      };
      token = customerPaymentProfileId;
    }

    if (paymentMethod === 4) {
      const encRouting = AES.encrypt(
        routingNumber,
        settings.api.mySecretKey
      ).toString();
      const encAccount = AES.encrypt(
        bankAccountNum,
        settings.api.mySecretKey
      ).toString();

      authPayment = {
        paymentType: 4,
        account_owner_name: nameOnAccount,
        routing_number: encRouting,
        account_number: encAccount,
        account_type: bankAccountType.value,
        account_owner_type: "individual",
        country: "US",
        currency: "usd"
      };
    }

    onSubmit({
      paymentType: paymentMethod,
      authPayment: JSON.stringify(authPayment),
      token
    });
  };

  return (
    <Fragment>
      <h3>Select a Payment Option</h3>

      <div className="row">
        <div className="col-md-6">
          <div
            className={`payment-gateway-card ${
              paymentMethod === 1 ? "active" : ""
            }`}
            onClick={() => setPaymentMethod(1)}
          >
            <h4>Credit Card / Debit Card</h4>
            <h2>${creditCardPayInfo.f_creditCardPayAmount}</h2>
            <p>Payment Includes {creditCardPayInfo.creditCardRate.percentage}% + {`$${creditCardPayInfo.creditCardRate.taxRate}`} Processing Charge</p>
          </div>
        </div>

        <div className="col-md-6">
          <div
            className={`payment-gateway-card ${
              paymentMethod === 4 ? "active" : ""
            }`}
            onClick={() => setPaymentMethod(4)}
          >
            <h4>Internet Banking</h4>
            <h2>${internetBankPayInfo.f_internetBankPayAmount}</h2>
            <p>Payment Includes {internetBankPayInfo.achTransferRate.percentage}% + {`$${internetBankPayInfo.achTransferRate.taxRate}`} Processing Charge</p>
          </div>
        </div>
      </div>

      {/* ---------- CARD UI ---------- */}
      {paymentMethod === 1 && (
        <Fragment>
          <div className="check_box_wrapper cm_dark_check mb-2">
            <label>Show deleted cards</label>
            <input
              type="checkbox"
              checked={showDeletedCards}
              onChange={() => setShowDeletedCards(!showDeletedCards)}
            />
            <span className="check_box"><span /></span>
          </div>

          {loading ? (
            <ProgramLoader />
          ) : (
            <ul className="card_list">
              {cardList.map(item => (
                <li
                  key={item.signature}
                  className={
                    customerPaymentProfileId === item.card.token
                      ? "active"
                      : ""
                  }
                >
                  <CreditCard
                    item={item}
                    customerPaymentProfileId={customerPaymentProfileId}
                    setCustomerPaymentProfileId={setCustomerPaymentProfileId}
                  />
                </li>
              ))}
            </ul>
          )}

          {isNewCard ? (
            <AddNewCard
              setIsNewCard={setIsNewCard}
              email={userEmail}
              userId={userId}
              getCards={getCards}
              campCode={campCode}
            />
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setIsNewCard(true)}
            >
              Add New Card
            </button>
          )}
        </Fragment>
      )}

      {/* ---------- ACH UI ---------- */}
      {paymentMethod === 4 && (
        <div className="card_inputs add_card_form">
          <CustomDropDown
            label="Select Account Type"
            options={accountTypes}
            onChange={setBankAccountType}
          />

          <div className="form-group">
            <label>Routing Number</label>
            <input
              type="number"
              className="form-control"
              placeholder="Routing Number"
              value={routingNumber}
              onChange={e => setRoutingNumber(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Account Number</label>
            <input
              type="number"
              className="form-control"
              placeholder="Account Number"
              value={bankAccountNum}
              onChange={e => setBankAccountNum(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Account Holder Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Account Holder Name"
              value={nameOnAccount}
              onChange={e => setNameOnAccount(e.target.value)}
            />
          </div>

        </div>
      )}

      <div className="text-center mt-4 d-flex justify-content-center gap-3">
        <button
          className="btn btn-primary"
          disabled={isPayDisabled}
          onClick={handleSubmit}
        >
          Pay Now
        </button>

        <button
          className="btn btn-secondary"
          onClick={onCancel}
        >
          Back
        </button>
      </div>

    </Fragment>
  );
};

export default GenericPaymentGateway;
